import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, ClickAwayListener } from "@mui/material";
import { tokens } from "../../theme";
import { setShaftRange } from "../../reducer/RawSurfaceSlice";
import { rangesOverlap } from "./shaftRanges";

const buttonSx = {
    appearance: "none",
    border: "none",
    cursor: "pointer",
    borderRadius: "5px",
    padding: "6px 10px",
    fontSize: "11.5px",
    fontWeight: 600,
    fontFamily: "inherit",
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    whiteSpace: "nowrap",
    "&:hover": { background: "rgba(255,255,255,0.18)" },
};

/**
 * Confirms which shaft a drag-selected range (from any of the three Raw Surface charts) belongs
 * to. Styled to match the mockup's .select-popover exactly - a solid ink chip positioned at the
 * drag's release point, not an MUI Popover: Popover's default backdrop intercepts the synthetic
 * click that follows a drag's mouseup and closes itself instantly (the same bug already
 * documented and fixed once in the mockup's own history), so outside-dismiss here is done by
 * hand via ClickAwayListener's mousedown mode instead of the default click mode.
 *
 * `pending` is ephemeral drag state ({ start, stop, anchorPosition } | null), owned by
 * RawSurfaceContent rather than Redux - it only exists between a drag ending and this popover
 * being resolved, never persisted.
 */
export default function RawSurfaceRangePopover({ pending, onClose }) {
    const dispatch = useDispatch();
    const { machineRange, motorRange } = useSelector((state) => state.rawSurface);
    const [warning, setWarning] = useState(null);

    // Matches the mockup's showSelectPopover: a freshly-opened popover always starts clear of
    // whatever warning a previous (now-resolved) selection may have left behind.
    useEffect(() => {
        setWarning(null);
    }, [pending]);

    if (!pending) return null;

    const assign = (shaft) => {
        const other = shaft === "machine" ? motorRange : machineRange;
        const otherLabel = shaft === "machine" ? "Motor" : "Machine";
        if (rangesOverlap(pending, other)) {
            // Matches the mockup: leave the selection and popover open so it can be adjusted,
            // rather than silently refusing or forcing it through.
            setWarning(`Overlaps the ${otherLabel} range — choose a range that doesn't overlap it.`);
            return;
        }
        dispatch(setShaftRange({ shaft, start: pending.start, stop: pending.stop }));
        onClose();
    };

    return (
        <ClickAwayListener mouseEvent="onMouseDown" onClickAway={onClose}>
            <Box
                role="menu"
                sx={{
                    position: "fixed",
                    zIndex: 1500,
                    top: pending.anchorPosition.top + 8,
                    left: pending.anchorPosition.left,
                    background: tokens.ink,
                    color: "#fff",
                    borderRadius: "8px",
                    boxShadow: tokens.shadow,
                    padding: "6px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                }}
            >
                {warning && (
                    <Box sx={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: tokens.reject,
                        background: tokens.rejectFill,
                        borderRadius: "5px",
                        padding: "6px 8px",
                        lineHeight: 1.35,
                        maxWidth: "220px",
                    }}>
                        {warning}
                    </Box>
                )}
                <Box sx={{ display: "flex", gap: "4px" }}>
                    <Box component="button" type="button" sx={buttonSx} onClick={() => assign("machine")}>
                        Set as Machine range
                    </Box>
                    <Box component="button" type="button" sx={buttonSx} onClick={() => assign("motor")}>
                        Set as Motor range
                    </Box>
                    <Box component="button" type="button" sx={buttonSx} onClick={onClose}>
                        Cancel
                    </Box>
                </Box>
            </Box>
        </ClickAwayListener>
    );
}
